// PageHero.js
// -----------------------------------------------------------------------------
// Full-bleed hero band with a background image, a colored overlay for contrast,
// a headline, optional subhead, and a children slot for CTAs. Use at the top of
// content pages.
//
// Props:
//   - image     : string   (default undef)   background image URL
//   - title     : node     (required)        hero headline
//   - subtitle  : node     (default undef)   supporting subhead
//   - eyebrow   : string   (default undef)   small uppercase kicker
//   - height    : 'sm'|'md'|'lg'|'full' (default 'md')  vertical size
//   - overlay   : 'none'|'light'|'medium'|'strong' (default 'medium') scrim
//   - align     : 'left'|'center'  (default 'center') text alignment
//   - children  : node     (default undef)   CTA slot (rendered under subtitle)
//   - sx        : object   (default {})       extra Joy sx overrides
// -----------------------------------------------------------------------------

import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Container from './Container';

const HEIGHTS = {
  sm: { xs: '38vh', md: '42vh' },
  md: { xs: '52vh', md: '62vh' },
  lg: { xs: '70vh', md: '78vh' },
  full: { xs: '88vh', md: '92vh' },
};

// Deep-navy scrim tints (primary.900 #071324). Alpha is required here, so the
// channel values are written literally; the hue matches the primary scale.
const OVERLAYS = {
  none: 'rgba(7, 19, 36, 0)',
  light: 'rgba(7, 19, 36, 0.30)',
  medium: 'rgba(7, 19, 36, 0.52)',
  strong: 'rgba(7, 19, 36, 0.70)',
};

const PageHero = ({
  image,
  title,
  subtitle,
  eyebrow,
  height = 'md',
  overlay = 'medium',
  align = 'center',
  children,
  sx = {},
}) => {
  // Zero-trust: constrain enum-like props; guard required title.
  const resolvedHeight = height in HEIGHTS ? HEIGHTS[height] : HEIGHTS.md;
  const resolvedOverlay = overlay in OVERLAYS ? OVERLAYS[overlay] : OVERLAYS.medium;
  const resolvedAlign = align === 'left' ? 'left' : 'center';
  const safeImage = typeof image === 'string' && image.length > 0 ? image : null;

  if (title === undefined || title === null || title === '') {
    return null;
  }

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: resolvedHeight,
        display: 'flex',
        alignItems: 'center',
        // Fallback brand background when no image is provided.
        bgcolor: 'primary.700',
        backgroundImage: safeImage ? `url("${encodeURI(safeImage)}")` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        ...sx,
      }}
    >
      {/* Overlay scrim for text legibility. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: resolvedOverlay,
        }}
      />

      <Container sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
        <Stack
          spacing={2.5}
          sx={{
            textAlign: resolvedAlign,
            alignItems: resolvedAlign === 'center' ? 'center' : 'flex-start',
            maxWidth: resolvedAlign === 'center' ? '820px' : '680px',
            mx: resolvedAlign === 'center' ? 'auto' : 0,
            py: { xs: 6, md: 8 },
          }}
        >
          {eyebrow ? (
            <Typography level="title" sx={{ color: 'accent.300' }}>
              {eyebrow}
            </Typography>
          ) : null}

          <Typography level="display" sx={{ color: 'common.white' }}>
            {title}
          </Typography>

          {subtitle ? (
            <Typography
              level="body-lg"
              sx={{ color: 'common.white', opacity: 0.92, maxWidth: '640px' }}
            >
              {subtitle}
            </Typography>
          ) : null}

          {children ? (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{
                pt: 1,
                width: { xs: '100%', sm: 'auto' },
                justifyContent: resolvedAlign === 'center' ? 'center' : 'flex-start',
              }}
            >
              {children}
            </Stack>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
};

PageHero.propTypes = {
  image: PropTypes.string,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  eyebrow: PropTypes.string,
  height: PropTypes.oneOf(['sm', 'md', 'lg', 'full']),
  overlay: PropTypes.oneOf(['none', 'light', 'medium', 'strong']),
  align: PropTypes.oneOf(['left', 'center']),
  children: PropTypes.node,
  sx: PropTypes.object,
};

export default PageHero;
